import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const DEFAULT_PARAMS = {
  elite: [
    { param_key: 'appearance', label: 'Appearance', param_type: 'score' },
    { param_key: 'aroma', label: 'Aroma', param_type: 'score' },
    { param_key: 'texture', label: 'Texture', param_type: 'score' },
    { param_key: 'taste', label: 'Taste', param_type: 'score' },
    { param_key: 'aftertaste', label: 'Aftertaste', param_type: 'score' },
    { param_key: 'overall', label: 'Overall', param_type: 'score' },
  ],
  consumer: [
    { param_key: 'appearance', label: 'Appearance', param_type: 'score' },
    { param_key: 'aroma', label: 'Aroma', param_type: 'score' },
    { param_key: 'texture', label: 'Texture', param_type: 'score' },
    { param_key: 'taste', label: 'Taste', param_type: 'score' },
    { param_key: 'value', label: 'Value for Money', param_type: 'score' },
  ],
};


const API = axios.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });

export default function TastePanel() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [sessionParams, setSessionParams] = useState(DEFAULT_PARAMS.elite);
  const [newParam, setNewParam] = useState({ label: '', param_type: 'score' });
  const [productSearch, setProductSearch] = useState('');
  const [showProductList, setShowProductList] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [cmpSel, setCmpSel] = useState({}); // session_id -> sample_name|null
  const [cmpSamples, setCmpSamples] = useState({}); // session_id -> [samples]
  const [cmpData, setCmpData] = useState(null);
  const [cmpSearch, setCmpSearch] = useState('');

  const togglePanelist = async (pid, isActive) => {
    try {
      await API.put('/taste/panelists/' + pid + '/active', { active: isActive ? 0 : 1 });
      toast.success(isActive ? 'Panelist disabled' : 'Panelist enabled');
      load();
    } catch (e) { toast.error('Update failed'); }
  };

  const toggleCmpSession = async (s) => {
    setCmpData(null);
    if (cmpSel[s.id] !== undefined) {
      setCmpSel(prev => { const n = { ...prev }; delete n[s.id]; return n; });
      return;
    }
    let smp = cmpSamples[s.id];
    if (!smp) {
      try { const r = await API.get('/taste/sessions/' + s.id + '/samples'); smp = r.data || []; } catch { smp = []; }
      setCmpSamples(prev => ({ ...prev, [s.id]: smp }));
    }
    setCmpSel(prev => ({ ...prev, [s.id]: smp.length > 1 ? '' : (smp[0]?.sample_name || 'Sample A') }));
  };

  const runCompare = async () => {
    const selections = Object.entries(cmpSel).map(([sid, sample]) => ({ session_id: parseInt(sid), sample_name: sample || 'Sample A' }));
    if (selections.length < 2) return toast.error('Select at least 2 sessions');
    if (Object.values(cmpSel).some(v => v === '')) return toast.error('Pick a sample for each multi-sample session');
    try { const r = await API.post('/taste/compare', { selections }); setCmpData(r.data); }
    catch (e) { toast.error(e.response?.data?.error || 'Compare failed'); }
  };
  const [filterType, setFilterType] = useState('');
  const [products, setProducts] = useState([]);
  const [newForm, setNewForm] = useState({ product_id: '', session_type: 'elite', session_date: new Date().toISOString().split('T')[0], batch_ref: '', notes: '' });
  const [panelists, setPanelists] = useState([]);
  const [profileData, setProfileData] = useState(null);
  const openProfile = async (pid) => {
    try { const r = await API.get('/taste/panelists/' + pid + '/history'); setProfileData(r.data); }
    catch (e) { toast.error('Could not load profile'); }
  };
  const [showPanelists, setShowPanelists] = useState(false);
  const [credForm, setCredForm] = useState({ id: null, username: '', password: '', tier: 'emerging' });
  const [showCred, setShowCred] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([API.get('/taste/sessions'), API.get('/products'), API.get('/taste/panelists?all=1')]).then(([s, p, pl]) => {
      setSessions(s.data);
      setProducts(p.data);
      setPanelists(pl.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveCred = async () => {
    if (!credForm.username) return toast.error('Username required');
    await API.put('/taste/panelists/' + credForm.id + '/credentials', credForm);
    toast.success('Credentials saved!');
    setShowCred(false);
    load();
  };

  const deleteSession = async (e, s) => {
    e.stopPropagation();
    if (!window.confirm('Delete session ' + s.session_code + '? Only possible if it has no feedback.')) return;
    try {
      await API.delete('/taste/sessions/' + s.id);
      toast.success('Session deleted');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Delete failed'); }
  };

  const createSession = async () => {
    if (!newForm.product_id) return toast.error('Select a product');
    const r = await API.post('/taste/sessions', { ...newForm, params: sessionParams });
    toast.success('Session ' + r.data.session_code + ' created!');
    setShowNew(false);
    setNewForm({ product_id: '', session_type: 'elite', session_date: new Date().toISOString().split('T')[0], batch_ref: '', notes: '' });
    load();
  };

  const filtered = sessions.filter(s => !filterType || s.session_type === filterType);

  const stats = {
    total: sessions.length,
    elite: sessions.filter(s => s.session_type === 'elite').length,
    consumer: sessions.filter(s => s.session_type === 'consumer').length,
    responses: sessions.reduce((a, s) => a + (s.consumer_count || 0), 0),
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Tasting Folks</div>
          <div className="page-subtitle">Manage evaluation sessions and consumer reviews</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" onClick={() => { setNewForm({...newForm, session_type:'elite'}); setSessionParams(DEFAULT_PARAMS.elite); setShowNew(true); }}>+ Tasting Folks Session</button>
          <button className="btn btn-secondary" onClick={() => { setNewForm({...newForm, session_type:'consumer'}); setSessionParams(DEFAULT_PARAMS.consumer); setShowNew(true); }}>+ Consumer Session</button>
          <button className="btn btn-secondary" onClick={() => setShowPanelists(v => !v)}>Tasting Folks Members</button>
          <button className={showCompare ? 'btn btn-primary' : 'btn btn-secondary'} onClick={() => setShowCompare(v => !v)}>📊 Compare Products</button>
        </div>
      </div>

      {showCompare && (() => {
        const SAMPLE_COLORS = ['#7B2D6B', '#E8541A', '#2e7d32', '#1565c0', '#f9a825', '#6d4c41', '#00838f', '#8e24aa'];
        const eliteSessions = sessions.filter(s => s.session_type === 'elite' && (s.product_name + ' ' + s.session_code).toLowerCase().includes(cmpSearch.toLowerCase()));
        let axes = [];
        if (cmpData) {
          const seen = new Set();
          cmpData.forEach(d => d.params.forEach(p => { if (!seen.has(p.param_key)) { seen.add(p.param_key); axes.push([p.param_key, p.label]); } }));
        }
        const radarData = cmpData ? axes.map(([key, lbl]) => {
          const row = { subject: lbl };
          cmpData.forEach(d => { const name = d.product_name + (cmpSamples[d.session_id]?.length > 1 ? ' — ' + d.sample_name : ''); row[name] = d.averages[key] != null ? d.averages[key] : null; });
          return row;
        }) : [];
        const barData = cmpData ? cmpData.map(d => ({ name: d.product_name + (cmpSamples[d.session_id]?.length > 1 ? ' — ' + d.sample_name : ''), score: d.averages.overall != null ? d.averages.overall : 0 })) : [];
        return (
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 700, color: 'var(--accent)', marginBottom: 10 }}>Compare Products (Tasting Folks sessions)</div>
            <input value={cmpSearch} onChange={e => setCmpSearch(e.target.value)} placeholder="Filter sessions..."
              style={{ width: 260, padding: '6px 10px', border: '1.5px solid #bbb', borderRadius: 8, fontSize: 13, marginBottom: 8, color: '#111', background: '#fff' }} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
              {eliteSessions.map(s => (
                <div key={s.id} style={{ border: cmpSel[s.id] !== undefined ? '2px solid var(--accent)' : '1px solid var(--border)', borderRadius: 10, padding: '6px 10px', cursor: 'pointer', background: cmpSel[s.id] !== undefined ? '#f9f3f8' : '#fff' }}
                  onClick={() => toggleCmpSession(s)}>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{s.product_name}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{s.session_code} · {s.session_date || ''}</div>
                  {cmpSel[s.id] !== undefined && (cmpSamples[s.id]?.length > 1) && (
                    <select value={cmpSel[s.id]} onClick={e => e.stopPropagation()} onChange={e => { setCmpSel(prev => ({ ...prev, [s.id]: e.target.value })); setCmpData(null); }}
                      style={{ marginTop: 4, fontSize: 11, padding: '2px 6px', borderRadius: 6, border: '1px solid var(--accent)' }}>
                      <option value="">— pick sample —</option>
                      {cmpSamples[s.id].map(sm => <option key={sm.sample_name} value={sm.sample_name}>{sm.sample_name}</option>)}
                    </select>
                  )}
                </div>
              ))}
            </div>
            <button className="btn btn-primary btn-sm" onClick={runCompare}>Compare ({Object.keys(cmpSel).length})</button>
            {cmpData && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
                <div>
                  <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 13 }}>Overall Likability</div>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={barData} margin={{ top: 5, right: 10, left: -20, bottom: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0e8ef" />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-18} textAnchor="end" />
                      <YAxis domain={[0, 10]} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="score" radius={[6, 6, 0, 0]}>
                        {barData.map((_, i) => <Cell key={i} fill={SAMPLE_COLORS[i % SAMPLE_COLORS.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 13 }}>All Parameters</div>
                  <ResponsiveContainer width="100%" height={280}>
                    <RadarChart data={radarData} outerRadius="68%">
                      <PolarGrid stroke="#e8dce6" />
                      <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10 }} />
                      <PolarRadiusAxis domain={[0, 10]} tick={{ fontSize: 9 }} />
                      {cmpData.map((d, i) => {
                        const name = d.product_name + (cmpSamples[d.session_id]?.length > 1 ? ' — ' + d.sample_name : '');
                        return <Radar key={name} name={name} dataKey={name} stroke={SAMPLE_COLORS[i % SAMPLE_COLORS.length]} fill={SAMPLE_COLORS[i % SAMPLE_COLORS.length]} fillOpacity={0.15} strokeWidth={2} />;
                      })}
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Tooltip />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
        {[['Total Sessions', stats.total, '#7B2D6B'], ['Tasting Folks', stats.elite, '#9d3d8a'], ['Consumer', stats.consumer, '#E8541A'], ['Consumer Responses', stats.responses, '#2e7d32']].map(([lbl,val,col]) => (
          <div key={lbl} className="stat-card"><div className="stat-value" style={{ color: col }}>{val}</div><div className="stat-label">{lbl}</div></div>
        ))}
      </div>

      {showPanelists && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, marginBottom: 14, color: 'var(--accent)' }}>Tasting Folks Members & Credentials</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
            Portal URL: <b>https://npd.bettergrow.in/panel</b>
          </div>
          {panelists.length === 0 ? <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No panelists yet.</div> : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ background: 'var(--bg-elevated)' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Name</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Role</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Tier</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Username</th>
                <th style={{ padding: '8px 12px', textAlign: 'center' }}>Status</th>
                <th style={{ padding: '8px 12px' }}></th>
              </tr></thead>
              <tbody>
                {panelists.map(p => (
                  <tr key={p.id} style={{ borderBottom: '1px solid var(--border)', opacity: p.active === 0 ? 0.5 : 1, cursor: 'pointer' }} onClick={() => openProfile(p.id)} title="View profile">
                    <td style={{ padding: '8px 12px', fontWeight: 600 }}>{p.name}</td>
                    <td style={{ padding: '8px 12px', color: 'var(--text-muted)' }}>{p.role || '—'}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{ background: p.tier === 'golden' ? '#fff8f0' : p.tier === 'gold' ? '#fffbf0' : p.tier === 'silver' ? '#f0f1ff' : '#f0f7ee', color: p.tier === 'golden' ? '#8B4513' : p.tier === 'gold' ? '#b8860b' : p.tier === 'silver' ? '#5c6bc0' : '#4a7c3f', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>
                        {p.tier === 'golden' ? '🏆 Golden' : p.tier === 'gold' ? '👑 Gold' : p.tier === 'silver' ? '⭐ Silver' : '🌱 Emerging'}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 12 }}>{p.username || <span style={{ color: 'var(--text-muted)' }}>Not set</span>}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                      <span style={{ background: p.active !== 0 ? '#e8f5e9' : '#fce8e6', color: p.active !== 0 ? '#2e7d32' : '#c62828', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>
                        {p.active !== 0 ? '● Active' : '○ Disabled'}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => { setCredForm({ id: p.id, username: p.username || '', password: '', tier: p.tier || 'emerging' }); setShowCred(true); }}>
                          🔑 Set Credentials
                        </button>
                        <button className={'btn btn-sm ' + (p.active !== 0 ? 'btn-danger' : 'btn-primary')} onClick={() => togglePanelist(p.id, p.active !== 0)}
                          style={{ minWidth: 80 }}>
                          {p.active !== 0 ? '⏸ Disable' : '▶ Enable'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
          {[['', 'All'], ['elite', 'Tasting Folks'], ['consumer', 'Consumer']].map(([val, lbl]) => (
            <button key={val} className={`btn btn-sm ${filterType === val ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilterType(val)}>{lbl}</button>
          ))}
          <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-muted)' }}>{filtered.length} sessions</span>
        </div>
        {loading ? <div className="empty-state">Loading...</div> :
        filtered.length === 0 ? <div className="empty-state"><div className="empty-state-icon">🧪</div><p>No sessions yet</p></div> :
        <div className="table-wrap">
          <table>
            <thead><tr><th>Session</th><th>Product</th><th>Type</th><th>Date</th><th>Responses</th><th>Avg Score</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {filtered.map(s => (
                <tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => navigate('/taste-panel/' + s.id)}>
                  <td><span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--accent)' }}>{s.session_code}</span></td>
                  <td style={{ fontWeight: 500 }}>{s.product_name}</td>
                  <td><span style={{ background: s.session_type === 'elite' ? '#f3e8ff' : '#e8f5e9', color: s.session_type === 'elite' ? '#7B2D6B' : '#2e7d32', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{s.session_type === 'elite' ? 'Tasting Folks' : '👥 Consumer'}</span></td>
                  <td style={{ fontSize: 12 }}>{s.session_date || '—'}</td>
                  <td style={{ textAlign: 'center', fontSize: 12 }}>{s.session_type === 'elite' ? (s.elite_count || 0) + ' panelists' : (s.consumer_count || 0)}</td>
                  <td>
                    {s.avg_score ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1, height: 6, background: '#f0e8ef', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ height: '100%', background: 'linear-gradient(90deg,#7B2D6B,#E8541A)', width: (s.avg_score / 10 * 100) + '%', borderRadius: 3 }}></div>
                        </div>
                        <b style={{ fontSize: 12 }}>{parseFloat(s.avg_score).toFixed(1)}</b>
                      </div>
                    ) : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}
                  </td>
                  <td><span style={{ background: s.status === 'open' ? '#fff3e0' : '#e8f5e9', color: s.status === 'open' ? '#e67e00' : '#2e7d32', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{s.status}</span></td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="btn btn-secondary btn-sm" onClick={e => { e.stopPropagation(); navigate('/taste-panel/' + s.id); }}>View →</button>
                    <button className="btn btn-danger btn-sm" style={{ marginLeft: 6, fontSize: 11 }} title="Delete (only if no feedback)" onClick={e => deleteSession(e, s)}>🗑</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>}
      </div>

      {showCred && (
        <div className="modal-overlay" onClick={() => setShowCred(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header"><div className="modal-title">Set Credentials</div><button className="modal-close" onClick={() => setShowCred(false)}>✕</button></div>
            <div style={{ padding: '8px 0' }}>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#888', display: 'block', marginBottom: 5 }}>Tier</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {[['emerging','🌱 Emerging'],['silver','⭐ Silver'],['gold','👑 Gold'],['golden','🏆 Golden']].map(([val,lbl]) => (
                    <button key={val} className={'btn btn-sm ' + (credForm.tier===val?'btn-primary':'btn-secondary')} onClick={() => setCredForm({...credForm, tier: val})}>{lbl}</button>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#888', display: 'block', marginBottom: 5 }}>Username</label>
                <input value={credForm.username} onChange={e => setCredForm({...credForm, username: e.target.value})} placeholder="e.g. hafis.k" style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #eee', borderRadius: 8 }} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#888', display: 'block', marginBottom: 5 }}>Password (leave blank to keep existing)</label>
                <input type="password" value={credForm.password} onChange={e => setCredForm({...credForm, password: e.target.value})} placeholder="New password" style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #eee', borderRadius: 8 }} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={saveCred}>Save</button>
              <button className="btn btn-secondary" onClick={() => setShowCred(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showNew && (
        <div className="modal-overlay" onClick={() => setShowNew(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <div className="modal-title">+ New {newForm.session_type === 'elite' ? 'Tasting Folks' : 'Consumer'} Session</div>
              <button className="modal-close" onClick={() => setShowNew(false)}>✕</button>
            </div>
            <div style={{ padding: '8px 0' }}>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#888', display: 'block', marginBottom: 5 }}>Session Type</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[['elite', 'Tasting Folks'], ['consumer', '👥 Consumer']].map(([val, lbl]) => (
                    <button key={val} className={`btn btn-sm ${newForm.session_type === val ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setNewForm({...newForm, session_type: val})}>{lbl}</button>
                  ))}
                </div>
              </div>
              {[['product_id', 'Product', 'select'], ['session_date', 'Session Date', 'date'], ['batch_ref', 'Batch / Trial Ref', 'text'], ['notes', 'Notes', 'textarea']].map(([key, lbl, type]) => (
                <div key={key} style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#888', display: 'block', marginBottom: 5 }}>{lbl}</label>
                  {type === 'select' ? (
                    <div style={{ position: 'relative' }}>
                      <input
                        value={newForm.product_id ? (products.find(p => String(p.id) === String(newForm.product_id))?.name || '') : productSearch}
                        onChange={e => { setProductSearch(e.target.value); setNewForm({...newForm, product_id: ''}); setShowProductList(true); }}
                        onFocus={() => setShowProductList(true)}
                        placeholder="Type to search product name or code..."
                        style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #bbb', borderRadius: 8, color: '#111', background: '#fff', boxSizing: 'border-box' }} />
                      {newForm.product_id && (
                        <button onClick={() => { setNewForm({...newForm, product_id: ''}); setProductSearch(''); }}
                          style={{ position: 'absolute', right: 8, top: 8, border: 'none', background: 'none', cursor: 'pointer', color: '#999' }}>✕</button>
                      )}
                      {showProductList && !newForm.product_id && (
                        <div style={{ position: 'absolute', zIndex: 50, top: '100%', left: 0, right: 0, maxHeight: 220, overflowY: 'auto', background: '#fff', border: '1px solid #ddd', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}>
                          {products.filter(p => !p.is_sfg && (p.name + ' ' + (p.product_code||'')).toLowerCase().includes(productSearch.toLowerCase())).slice(0, 40).map(p => (
                            <div key={p.id} onClick={() => { setNewForm({...newForm, product_id: p.id}); setShowProductList(false); setProductSearch(''); }}
                              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f5f5f5' }}
                              onMouseEnter={e => e.currentTarget.style.background = '#f9f3f8'}
                              onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                              <b>{p.name}</b> <span style={{ color: '#999', fontSize: 11 }}>({p.product_code})</span>
                            </div>
                          ))}
                          {products.filter(p => !p.is_sfg && (p.name + ' ' + (p.product_code||'')).toLowerCase().includes(productSearch.toLowerCase())).length === 0 && (
                            <div style={{ padding: '10px 12px', color: '#999', fontSize: 12 }}>No products match</div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : type === 'textarea' ? (
                    <textarea value={newForm[key]} onChange={e => setNewForm({...newForm, [key]: e.target.value})} rows={2} style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #eee', borderRadius: 8, resize: 'vertical' }} />
                  ) : (
                    <input type={type} value={newForm[key]} onChange={e => setNewForm({...newForm, [key]: e.target.value})} style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #eee', borderRadius: 8 }} />
                  )}
                </div>
              ))}
            </div>
                          <div style={{ marginTop: 14, borderTop: '1px solid var(--border, #eee)', paddingTop: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#888', marginBottom: 6 }}>Scoring Parameters</div>
                {sessionParams.map((p, i) => (
                  <div key={p.param_key + i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 13 }}>
                    <span style={{ flex: 1 }}>{p.label} <span style={{ fontSize: 10, color: '#999' }}>({p.param_type})</span></span>
                    <button className="btn btn-danger btn-sm" style={{ fontSize: 10, padding: '2px 8px' }}
                      onClick={() => setSessionParams(list => list.filter((_, j) => j !== i))}>✕</button>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 6, marginTop: 8, width: '100%' }}>
                  <input placeholder="New parameter (e.g. Crunchiness)" value={newParam.label} autoComplete="off"
                    onChange={e => setNewParam(np => ({ ...np, label: e.target.value }))}
                    style={{ flex: '1 1 auto', minWidth: 200, height: 38, padding: '6px 12px', border: '1.5px solid #bbb', borderRadius: 8, fontSize: 14, color: '#111111', background: '#ffffff', WebkitTextFillColor: '#111111', caretColor: '#111111' }} />
                  <select value={newParam.param_type} onChange={e => setNewParam(np => ({ ...np, param_type: e.target.value }))}
                    style={{ width: 130, height: 38, padding: '6px 8px', borderRadius: 8, border: '1.5px solid #bbb', fontSize: 13, color: '#111111', background: '#ffffff' }}>
                    <option value="score">Score 0-10</option>
                    <option value="text">Text</option>
                  </select>
                  <button className="btn btn-secondary btn-sm" style={{ height: 38 }} onClick={() => {
                    if (!newParam.label.trim()) return;
                    const key = newParam.label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_');
                    if (sessionParams.some(p => p.param_key === key)) return;
                    setSessionParams(list => [...list, { param_key: key, label: newParam.label.trim(), param_type: newParam.param_type }]);
                    setNewParam({ label: '', param_type: 'score' });
                  }}>+ Add</button>
                </div>
              </div>
                          <div className="modal-footer">
              <button className="btn btn-primary" onClick={createSession}>Create Session</button>
              <button className="btn btn-secondary" onClick={() => setShowNew(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {profileData && (() => {
        const ATTRS = [['appearance','Appearance'],['aroma','Aroma'],['texture','Texture'],['taste','Taste'],['aftertaste','Aftertaste'],['overall','Overall']];
        const chartData = ATTRS.map(([k, lbl]) => ({ name: lbl, Mine: profileData.param_averages.mine[k], Panel: profileData.param_averages.panel[k] }));
        const st = profileData.stats || {};
        return (
          <div onClick={() => setProfileData(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,8,18,0.55)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div onClick={e => e.stopPropagation()} style={{ width: 860, maxWidth: '97%', maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: 14, padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--accent)' }}>{profileData.panelist.name} <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>{profileData.panelist.role || 'Panelist'}{profileData.panelist.active === 0 ? ' · DISABLED' : ''}</span></div>
                <button className="btn btn-secondary btn-sm" onClick={() => setProfileData(null)}>✕ Close</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, margin: '12px 0' }}>
                {[['Sessions', st.sessions], ['Samples Scored', st.samples_scored], ['Avg Overall Given', st.avg_overall != null ? st.avg_overall : '—'], ['Last Activity', st.last_activity ? String(st.last_activity).slice(0, 10) : '—']].map(([lbl, val]) => (
                  <div key={lbl} style={{ background: '#f9f3f8', borderRadius: 10, padding: '10px 12px' }}>
                    <div style={{ fontSize: 10, textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: 700 }}>{lbl}</div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--accent)' }}>{val != null ? val : '—'}</div>
                  </div>
                ))}
              </div>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Scoring Tendency — vs Panel Average</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0e8ef" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis domain={[0, 10]} tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="Mine" fill="#7B2D6B" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Panel" fill="#E8541A" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div style={{ fontWeight: 700, fontSize: 13, margin: '12px 0 6px' }}>History ({profileData.history.length})</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead><tr style={{ textAlign: 'left', color: 'var(--text-muted)', fontSize: 10, textTransform: 'uppercase' }}>
                  <th style={{ padding: 6 }}>Date</th><th>Product</th><th>Sample</th><th>Overall</th><th>Recommendation</th><th>Comments</th>
                </tr></thead>
                <tbody>
                  {profileData.history.map(h => (
                    <tr key={h.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: 6, whiteSpace: 'nowrap' }}>{h.created_at ? String(h.created_at).slice(0, 10) : (h.session_date || '')}</td>
                      <td>{h.product_name || ''} <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>{h.session_code}</span></td>
                      <td>{h.sample_name || 'Sample A'}</td>
                      <td style={{ fontWeight: 700, color: 'var(--accent)' }}>{h.overall != null ? h.overall : '—'}</td>
                      <td style={{ fontSize: 11 }}>{h.recommendation || ''}</td>
                      <td style={{ fontSize: 11, color: 'var(--text-muted)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={h.comments || ''}>{h.comments || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
